import { useEffect, useState } from "react";
import {
	collection,
	setDoc,
	getDocs,
	doc,
	deleteDoc,
} from "firebase/firestore";
import { reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, auth } from "../firebase";
import { useTranslation } from "../i18n/i18n";
import {
	UserCheck,
	UserCog,
	CalendarSync,
	AlertTriangle,
	X,
	Trash2,
	CircleChevronDown,
	CircleChevronUp,
} from "lucide-react";
import StickyTitle from "../components/StickyTitle/StickyTitle";
import Loader from "../components/Loader/Loader";
import Modal from "../components/Modal/Modal";
import ReauthForm from "../components/AuthForm/ReauthForm";
import { toast } from "react-toastify";
import "../styles/admin.css";

const Admin = () => {
	const { translate } = useTranslation();
	const [email, setEmail] = useState("");
	const [whitelist, setWhitelist] = useState([]);
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showAllWhitelist, setShowAllWhitelist] = useState(false);
	const [showAllUsers, setShowAllUsers] = useState(false);
	const [showReauthModal, setShowReauthModal] = useState(false);
	const [pendingRoleChange, setPendingRoleChange] = useState(null);
	const [reauthPassword, setReauthPassword] = useState("");
	const [processingRecurring, setProcessingRecurring] = useState(false);

	// States for whitelist deletion confirmation modal
	const [showDeleteWhitelistModal, setShowDeleteWhitelistModal] =
		useState(false);
	const [emailToDelete, setEmailToDelete] = useState(null);

	// Initialize Firebase Functions
	const functions = getFunctions(undefined, "europe-west1");
	const manualProcessRecurring = httpsCallable(
		functions,
		"manualProcessRecurringTransactions"
	);

	const fetchWhitelist = async () => {
		const snapshot = await getDocs(collection(db, "whitelistedEmails"));
		const emails = snapshot.docs
			.map(doc => {
				const data = doc.data();
				return {
					id: doc.id,
					email: doc.id,
					createdAt: data.createdAt || null,
				};
			})
			.sort((a, b) => {
				return new Date(b.createdAt) - new Date(a.createdAt);
			});
		setWhitelist(emails);
	};

	const fetchUsers = async () => {
		const snapshot = await getDocs(collection(db, "users"));
		const usersData = snapshot.docs
			.map(doc => ({
				id: doc.id,
				...doc.data(),
			}))
			.sort((a, b) => {
				// Sort newest to oldest
				const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
				const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
				return dateB - dateA;
			});
		return usersData;
	};

	useEffect(() => {
		const fetchData = async () => {
			await fetchWhitelist();
			const usersData = await fetchUsers();
			setUsers(usersData);
			setLoading(false);
		};
		fetchData();
	}, []);

	const handleAddToWhitelist = async e => {
		e.preventDefault();
		if (!email.trim()) return toast.warn(translate("admin.emailRequired"));
		const trimmedEmail = email.trim().toLowerCase();
		try {
			await setDoc(doc(db, "whitelistedEmails", trimmedEmail), {
				createdAt: new Date().toISOString(),
			});
			setEmail("");
			toast.success(translate("admin.emailAdded"));
			await fetchWhitelist();
		} catch (err) {
			console.error("Error adding email: ", err);
			toast.error(translate("admin.emailAddError"));
		}
	};

	const handleDeleteFromWhitelist = async emailItem => {
		setEmailToDelete(emailItem);
		setShowDeleteWhitelistModal(true);
	};

	const confirmDeleteFromWhitelist = async () => {
		if (!emailToDelete) return;

		try {
			await deleteDoc(doc(db, "whitelistedEmails", emailToDelete.id));
			toast.success(translate("admin.emailRemoved"));
			await fetchWhitelist();
		} catch (err) {
			console.error("Error deleting email: ", err);
			toast.error(translate("admin.emailRemoveError"));
		} finally {
			setShowDeleteWhitelistModal(false);
			setEmailToDelete(null);
		}
	};

	const reauthenticateUser = async password => {
		const user = auth.currentUser;
		if (!user || !user.email) {
			throw new Error(translate("error.userNotLoggedIn"));
		}

		const credential = EmailAuthProvider.credential(user.email, password);
		await reauthenticateWithCredential(user, credential);
	};

	const executeRoleChange = async (
		userId,
		newRole,
		retryWithReauth = false
	) => {
		const toastId = toast.loading(translate("admin.updatingRole"));

		try {
			if (retryWithReauth && reauthPassword) {
				await reauthenticateUser(reauthPassword);
			}

			const token = await auth.currentUser.getIdToken(true);
			const response = await fetch(process.env.REACT_APP_UPDATE_ROLE_URL, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ targetUserId: userId, newRole }),
			});

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));

				if (
					(response.status === 401 ||
						errorData.code === "auth/requires-recent-login") &&
					!retryWithReauth
				) {
					toast.dismiss(toastId);
					setPendingRoleChange({ userId, newRole });
					setShowReauthModal(true);
					return;
				}

				throw new Error(errorData.message || `HTTP error ${response.status}`);
			}

			toast.update(toastId, {
				render: translate("admin.roleUpdated"),
				type: "success",
				isLoading: false,
				autoClose: 5000,
			});

			setPendingRoleChange(null);
			setReauthPassword("");
			setShowReauthModal(false);

			const updatedUsers = await fetchUsers();
			setUsers(updatedUsers);
		} catch (err) {
			console.error("Error updating role: ", err.message);
			toast.update(toastId, {
				render: translate("admin.roleUpdateError") + " " + err.message,
				type: "error",
				isLoading: false,
				autoClose: 5000,
			});
		}
	};

	const handleRoleChange = async (userId, newRole) => {
		await executeRoleChange(userId, newRole, false);
	};

	const handleReauthSubmit = async e => {
		e.preventDefault();
		if (!reauthPassword.trim()) {
			toast.error(translate("error.passwordRequired"));
			return;
		}

		try {
			await executeRoleChange(
				pendingRoleChange.userId,
				pendingRoleChange.newRole,
				true
			);
		} catch (err) {
			toast.error(
				translate("error.reauthenticationFailed") + " " + err.message
			);
		}
	};

	// Function to manually process recurring transactions
	const handleManualProcessRecurring = async () => {
		setProcessingRecurring(true);
		const toastId = toast.loading(translate("admin.processingRecurring"));

		try {
			const result = await manualProcessRecurring({});
			console.log("Recurring processing result:", result.data);

			toast.update(toastId, {
				render: translate("admin.recurringProcessed", {
					processed: result.data.processed,
					failed: result.data.failed,
					skipped: result.data.skipped,
				}),
				type: "success",
				isLoading: false,
				autoClose: 5000,
			});
		} catch (error) {
			console.error("Error processing recurring transactions:", error);

			toast.update(toastId, {
				render: translate("admin.recurringProcessError") + " " + error.message,
				type: "error",
				isLoading: false,
				autoClose: 5000,
			});
		} finally {
			setProcessingRecurring(false);
		}
	};

	if (loading) {
		return (
			<div className="content">
				<StickyTitle title={translate("admin.title")} />
				<div className="page-content">
					<Loader
						fullscreen={true}
						loadingTextKey="admin.loading"
						theme="light"
						translate={translate}
					/>
				</div>
			</div>
		);
	}

	const whitelistToShow = showAllWhitelist ? whitelist : whitelist.slice(0, 3);
	const usersToShow = showAllUsers ? users : users.slice(0, 3);

	return (
		<div className="content admin">
			<StickyTitle title={translate("admin.title")} />

			{/* Reauth Modal */}
			<Modal
				isOpen={showReauthModal}
				onClose={() => {
					setShowReauthModal(false);
					setPendingRoleChange(null);
					setReauthPassword("");
				}}
				animated={true}
			>
				<ReauthForm
					reauthPassword={reauthPassword}
					setReauthPassword={setReauthPassword}
					onCancel={() => {
						setShowReauthModal(false);
						setPendingRoleChange(null);
						setReauthPassword("");
					}}
					onSubmit={handleReauthSubmit}
					translate={translate}
				/>
			</Modal>

			{/* Whitelist deletion confirmation modal */}
			<Modal
				isOpen={showDeleteWhitelistModal}
				onClose={() => {
					setShowDeleteWhitelistModal(false);
					setEmailToDelete(null);
				}}
				animated={true}
				modalType="delete"
			>
				<div className="reauth-modal-content glass-effect">
					<div className="settings-title-container">
						<AlertTriangle size={20} color="#ff4444" />
						<h2>{translate("admin.removeFromWhitelist")}</h2>
					</div>
					<div className="layout-row design-form">
						<p>
							{translate("admin.confirmDeleteWhitelist", {
								email: emailToDelete?.email || "",
							})}
						</p>
					</div>
					<div className="ios-buttons">
						<button
							className="ios-button"
							onClick={() => {
								setShowDeleteWhitelistModal(false);
								setEmailToDelete(null);
							}}
						>
							<X size={18} />
							{translate("action.cancel")}
						</button>
						<button
							className="ios-button warning"
							onClick={confirmDeleteFromWhitelist}
						>
							<Trash2 size={18} />
							{translate("admin.delete")}
						</button>
					</div>
				</div>
			</Modal>

			<div className="page-content">
				{/* Section for processing recurring transactions */}
				<div className="card glass-effect settings-container">
					<div className="settings-title-container">
						<CalendarSync size={20} />
						<h2>{translate("admin.recurringTransactions")}</h2>
					</div>
					<div className="item-container">
						<p>{translate("admin.recurringDescription")}</p>
						<button
							onClick={handleManualProcessRecurring}
							disabled={processingRecurring}
							className="ios-button action"
						>
							{translate("admin.processRecurring")}
						</button>
					</div>
				</div>
				<div className="card glass-effect settings-container">
					<div className="settings-title-container">
						<UserCheck size={20} />
						<h2>{translate("admin.manageWhitelist")}</h2>
					</div>
					<form
						onSubmit={handleAddToWhitelist}
						className="design-form layout-row item-container"
					>
						<input
							className="field layout-col-3-4 "
							type="email"
							value={email}
							onChange={e => setEmail(e.target.value)}
							placeholder={translate("admin.emailPlaceholder")}
							required
						/>
						<button type="submit" className="ios-button action layout-col-1-4">
							{translate("admin.addToWhitelist")}
						</button>
					</form>
					<ul className="admin-whitelist-container">
						{whitelistToShow.map(item => (
							<li className="item-container admin-user-card" key={item.id}>
								<div className="cell">{item.email}</div>
								<button
									onClick={() => handleDeleteFromWhitelist(item)}
									className="ios-button warning"
								>
									{translate("admin.delete")}
								</button>
							</li>
						))}
					</ul>
					{whitelist.length > 3 && (
						<button
							className="ios-button ghost small-button"
							onClick={() => setShowAllWhitelist(prev => !prev)}
						>
							{showAllWhitelist ? (
								<>
									<CircleChevronUp size={18} />
									{translate("admin.seeLess")}
								</>
							) : (
								<>
									<CircleChevronDown size={18} />
									{translate("admin.seeMore")}
								</>
							)}
						</button>
					)}
				</div>
				<div className="card glass-effect settings-container">
					<div className="settings-title-container">
						<UserCog size={20} />
						<h2>{translate("admin.manageUsers")}</h2>
					</div>
					<div className="admin-user-cards">
						<div className="admin-user-header">
							<div className="cell">{translate("admin.email")}</div>
							<div className="cell">{translate("admin.emailVerified")}</div>
							<label htmlFor="roleSelect" className="cell">
								{translate("admin.changeRole")}
							</label>
						</div>
						{usersToShow.map(user => (
							<div key={user.id} className="admin-user-card">
								<div className="cell">{user.email}</div>
								<div className="cell">
									{user.emailVerified
										? translate("admin.yes")
										: translate("admin.no")}
								</div>
								<div className="cell select-wrapper">
									<select
										id="roleSelect"
										value={user.role}
										onChange={e => handleRoleChange(user.id, e.target.value)}
										className="custom-select"
									>
										<option value="user">{translate("admin.roleUser")}</option>
										<option value="admin">
											{translate("admin.roleAdmin")}
										</option>
									</select>
								</div>
							</div>
						))}
					</div>
					{users.length > 3 && (
						<button
							className="ios-button ghost small-button"
							onClick={() => setShowAllUsers(prev => !prev)}
						>
							{showAllUsers ? (
								<>
									<CircleChevronUp size={18} />
									{translate("admin.seeLess")}
								</>
							) : (
								<>
									<CircleChevronDown size={18} />
									{translate("admin.seeMore")}
								</>
							)}
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default Admin;
